/**
 * Hisbah supplier-portal async bulk-sheet tracking endpoints.
 * Backend: SupplierOfferController (offers/bulk-jobs*). Every call is
 * server-side scoped to PartnerPortalUser::supplier_id via the `token` header.
 */
import axios from 'axios'
import fileDownload from 'js-file-download'
import {API_URL} from '../../../../tenant/apiBase'

const BASE = `${API_URL}/api/partner-portal/offers/bulk-jobs`

function authHeaders(token?: string, language?: string) {
  return {token: token || '', language: language || 'en'}
}

export interface IBulkJob {
  job_id: number
  operation_type: string
  original_filename: string
  status: string
  total_rows: number
  processed_rows: number
  failed_rows: number
  error_summary: string | null
  has_result: boolean
  // Async ADD: ambiguous variants the worker parked for a post-job review.
  review_required?: boolean
  pending_count?: number
  queued_at: string | null
  finished_at: string | null
}

export interface IBulkJobsResponse {
  data: IBulkJob[]
  total: number
}

export interface IBulkJobNotifications {
  unread_count: number
  items: IBulkJob[]
}

export function getBulkJobs(
  params: {page?: number; limit?: number; status?: string},
  token?: string,
  language?: string,
) {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.status) qs.set('status', params.status)
  const s = qs.toString()
  return axios.get<IBulkJobsResponse>(`${BASE}${s ? `?${s}` : ''}`, {
    headers: authHeaders(token, language),
  })
}

export function getBulkJobNotifications(token?: string, language?: string) {
  return axios.get<IBulkJobNotifications>(`${BASE}/notifications`, {
    headers: authHeaders(token, language),
  })
}

export function markAllBulkJobsSeen(token?: string, language?: string) {
  return axios.post(`${BASE}/notifications/read-all`, {}, {headers: authHeaders(token, language)})
}

/** Async ADD review: the ambiguous variants the worker parked for this job. */
export function getPendingVariants(jobId: number, token?: string, language?: string) {
  return axios.get<{data: any[]}>(`${BASE}/${jobId}/pending-variants`, {
    headers: authHeaders(token, language),
  })
}

/** Mark one parked barcode resolved (after the supplier handled it). */
export function resolvePendingVariant(
  jobId: number,
  barcode: string,
  token?: string,
  language?: string,
) {
  return axios.post<{ok?: boolean; remaining?: number}>(
    `${BASE}/${jobId}/resolve`,
    {barcode},
    {headers: authHeaders(token, language)},
  )
}

/** Download the generated result sheet as a blob (token via header, not URL). */
export async function downloadBulkJobResult(job: IBulkJob, token?: string, language?: string) {
  const res = await axios.get(`${BASE}/${job.job_id}/result`, {
    headers: authHeaders(token, language),
    responseType: 'blob',
  })
  const base = job.original_filename || `bulk-result-${job.job_id}.xlsx`
  const name = base.toLowerCase().endsWith('.xlsx') ? `result-${base}` : `result-${base}.xlsx`
  fileDownload(res.data, name)
}
